/* DECO Music Player — v4: cross-page state + optional seamless store navigation.
   Preserves the actual playing audio element on supported same-origin routes.
   Standard page reloads still depend on the browser's audible-autoplay policy. */
(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-deco-music-player]';
  const STORAGE_KEY = 'deco-music-player:session-v3';
  const clamp = (number, min, max) => Math.min(Math.max(number, min), max);
  const formatTime = (value) => {
    if (!Number.isFinite(value) || value < 0) return '0:00';
    const total = Math.floor(value);
    const m = Math.floor(total / 60);
    return `${m}:${String(total % 60).padStart(2, '0')}`;
  };
  const escapeHTML = (value = '') => String(value).replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
  }[char]));

  class DecoMusicPlayer {
    constructor(root) {
      this.root = root;
      this.audio = root.querySelector('.dmp-audio');
      this.panel = root.querySelector('[data-dmp-panel]');
      this.scrim = root.querySelector('[data-dmp-scrim]');
      this.openButton = root.querySelector('[data-dmp-open]');
      this.closeButton = root.querySelector('[data-dmp-close]');
      this.toggleButtons = [...root.querySelectorAll('[data-dmp-toggle]')];
      this.prevButton = root.querySelector('[data-dmp-prev]');
      this.nextButton = root.querySelector('[data-dmp-next]');
      this.shuffleButton = root.querySelector('[data-dmp-shuffle]');
      this.repeatButton = root.querySelector('[data-dmp-repeat]');
      this.repeatLabel = root.querySelector('[data-dmp-repeat-label]');
      this.muteButton = root.querySelector('[data-dmp-mute]');
      this.progress = root.querySelector('[data-dmp-progress]');
      this.queue = root.querySelector('[data-dmp-queue]');
      this.currentText = root.querySelector('[data-dmp-current]');
      this.durationText = root.querySelector('[data-dmp-duration]');
      this.title = root.querySelector('[data-dmp-title]');
      this.artist = root.querySelector('[data-dmp-artist]');
      this.cover = root.querySelector('[data-dmp-cover]');
      this.launcherArt = root.querySelector('[data-dmp-launcher-art]');
      this.ambientImage = root.querySelector('[data-dmp-ambient-image]');
      this.status = root.querySelector('[data-dmp-status]');
      this.viewButtons = [...root.querySelectorAll('[data-dmp-view]')];

      const configNode = root.querySelector('[data-dmp-data]');
      let data = {};
      try { data = configNode ? JSON.parse(configNode.textContent) : {}; }
      catch (error) { console.error('[DECO Music] Track configuration error:', error); }
      this.settings = {
        remember: data.settings?.remember !== false,
        autoplayNext: data.settings?.autoplayNext !== false,
        seamlessNavigation: data.settings?.seamlessNavigation !== false,
        defaultVolume: clamp(Number(data.settings?.defaultVolume ?? 0.8) || 0, 0, 1)
      };
      this.tracks = Array.isArray(data.tracks) ? data.tracks.filter(
        (track) => track && typeof track.audio === 'string' && track.audio.trim()
      ) : [];

      this.index = 0;
      this.sourceIndex = -1;
      this.pendingPosition = null;
      this.resumeAfterMetadata = false;
      this.intendedPlaying = false;
      this.repeatMode = 'off';
      this.shuffle = false;
      this.isSeeking = false;
      this.isOpen = false;
      this.lastFocused = null;
      this.lastSave = 0;
      this.saveTimer = null;
      this.statusTimer = null;
      this.closeTimer = null;
      this.playRequest = 0;
      this.destroyed = false;
      this.onPageHide = this.onPageHide.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onQueueClick = this.onQueueClick.bind(this);
      this.onNavigation = this.onNavigation.bind(this);
      this.onHistoryNavigation = this.onHistoryNavigation.bind(this);
      this.navigationController = null;
      this.navigationSequence = 0;
      this.didSeamlessNavigate = false;
      this.state = this.readState();

      if (this.audio && this.panel && this.scrim && this.openButton) this.init();
      else console.warn('[DECO Music] Required player elements are missing.');
    }

    readState() {
      if (!this.settings.remember) return null;
      try {
        const parsed = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
        return parsed && typeof parsed === 'object' ? parsed : null;
      } catch { return null; }
    }

    savedPosition() {
      if (this.pendingPosition !== null) return this.pendingPosition;
      return Number.isFinite(this.audio.currentTime) ? this.audio.currentTime : 0;
    }

    saveState(force = false) {
      if (!this.settings.remember || !this.tracks.length || this.destroyed) return;
      if (!force && Date.now() - this.lastSave < 750) return;
      this.lastSave = Date.now();
      const track = this.tracks[this.index];
      if (!track) return;
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
          trackId: String(track.id),
          audioUrl: track.audio,
          position: this.savedPosition(),
          shouldPlay: this.intendedPlaying,
          volume: this.audio.volume,
          muted: this.audio.muted,
          shuffle: this.shuffle,
          repeat: this.repeatMode
        }));
      } catch { /* Restricted storage: playback still works within the page. */ }
    }

    scheduleSave() {
      if (!this.settings.remember || this.saveTimer) return;
      this.saveTimer = window.setTimeout(() => {
        this.saveTimer = null;
        this.saveState(true);
      }, 850);
    }

    init() {
      // Always bind the launcher, even when the merchant has not added tracks yet.
      this.bindEvents();
      this.panel.inert = true;
      this.setView('now');
      const saved = this.state;
      if (saved) {
        this.audio.volume = Number.isFinite(Number(saved.volume))
          ? clamp(Number(saved.volume), 0, 1) : this.settings.defaultVolume;
        this.audio.muted = saved.muted === true;
        this.shuffle = saved.shuffle === true;
        this.repeatMode = ['off', 'all', 'one'].includes(saved.repeat) ? saved.repeat : 'off';
      } else {
        this.audio.volume = this.settings.defaultVolume;
      }
      this.updateModeUI();

      if (!this.tracks.length) {
        this.root.classList.add('dmp-empty');
        this.toggleButtons.forEach((button) => { button.disabled = true; });
        [this.prevButton, this.nextButton, this.progress].forEach((el) => { if (el) el.disabled = true; });
        this.notify('Add a direct audio URL to a track in the Theme Editor.');
        return;
      }

      const savedIndex = saved ? this.tracks.findIndex((track) =>
        String(track.id) === String(saved.trackId) || track.audio === saved.audioUrl
      ) : -1;
      this.index = savedIndex >= 0 ? savedIndex : 0;
      this.renderQueue();
      this.paintTrack(this.index);
      this.updatePlayUI();

      // On a fresh session, no source needs to play or load automatically.
      // A saved paused session restores the timestamp but stays paused.
      if (savedIndex >= 0) {
        const position = Number.isFinite(Number(saved.position)) ? Math.max(0, Number(saved.position)) : 0;
        this.intendedPlaying = saved.shouldPlay === true;
        this.resumeAfterMetadata = this.intendedPlaying;
        this.ensureSource(position);
        this.updateTimeline(position);
        if (this.resumeAfterMetadata && this.audio.readyState >= 1) {
          this.resumeAfterMetadata = false;
          this.tryPlay(true);
        }
      }
    }

    bindEvents() {
      this.openButton.addEventListener('click', () => this.open());
      this.closeButton?.addEventListener('click', () => this.close());
      this.scrim.addEventListener('click', () => this.close());
      this.toggleButtons.forEach((button) => button.addEventListener('click', (event) => {
        event.stopPropagation();
        this.toggle();
      }));
      this.prevButton?.addEventListener('click', () => this.previous());
      this.nextButton?.addEventListener('click', () => this.next(true));
      this.shuffleButton?.addEventListener('click', () => {
        this.shuffle = !this.shuffle;
        this.updateModeUI();
        this.saveState(true);
      });
      this.repeatButton?.addEventListener('click', () => {
        this.repeatMode = this.repeatMode === 'off' ? 'all' : this.repeatMode === 'all' ? 'one' : 'off';
        this.updateModeUI();
        this.saveState(true);
      });
      this.muteButton?.addEventListener('click', () => {
        this.audio.muted = !this.audio.muted;
        this.updateModeUI();
        this.saveState(true);
      });
      this.viewButtons.forEach((button) => button.addEventListener('click', () =>
        this.setView(button.getAttribute('data-dmp-view'))
      ));
      this.queue?.addEventListener('click', this.onQueueClick);

      this.progress?.addEventListener('pointerdown', () => { this.isSeeking = true; });
      this.progress?.addEventListener('input', () => {
        if (!Number.isFinite(this.audio.duration) || this.audio.duration <= 0) return;
        const position = this.audio.duration * Number(this.progress.value) / 1000;
        if (this.currentText) this.currentText.textContent = formatTime(position);
        this.updateRangeFill(this.progress, Number(this.progress.value) / 10);
      });
      this.progress?.addEventListener('change', () => {
        this.isSeeking = false;
        if (this.ensureSource() && Number.isFinite(this.audio.duration)) {
          this.pendingPosition = null;
          try { this.audio.currentTime = this.audio.duration * Number(this.progress.value) / 1000; }
          catch { /* Media might not yet be seekable. */ }
        }
        this.updateTimeline();
        this.saveState(true);
      });

      this.audio.addEventListener('loadedmetadata', () => {
        this.applyPendingPosition();
        if (this.durationText) this.durationText.textContent = formatTime(this.audio.duration);
        this.updateTimeline();
        if (this.resumeAfterMetadata) {
          this.resumeAfterMetadata = false;
          this.tryPlay(true);
        }
      });
      this.audio.addEventListener('canplay', () => {
        if (this.pendingPosition !== null) this.applyPendingPosition();
      });
      this.audio.addEventListener('durationchange', () => this.updateTimeline());
      this.audio.addEventListener('timeupdate', () => {
        this.updateTimeline();
        this.scheduleSave();
        this.updateMediaPosition();
      });
      this.audio.addEventListener('seeked', () => { this.updateTimeline(); this.saveState(true); });
      this.audio.addEventListener('play', () => {
        this.intendedPlaying = true;
        this.updatePlayUI();
        this.saveState(true);
      });
      this.audio.addEventListener('pause', () => {
        // Pause can be emitted during a track change; it is not always a user command.
        this.updatePlayUI();
        this.saveState(true);
      });
      this.audio.addEventListener('ended', () => {
        if (this.repeatMode === 'one') {
          this.audio.currentTime = 0;
          this.tryPlay(false);
        } else if (this.settings.autoplayNext || this.repeatMode === 'all') {
          this.next(false);
        } else {
          this.intendedPlaying = false;
            this.updatePlayUI();
          this.saveState(true);
        }
      });
      this.audio.addEventListener('error', () => {
        if (this.sourceIndex >= 0) this.notify('Could not load this song. Check its direct audio URL.');
      });

      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onNavigation);
      document.addEventListener('submit', this.onNavigation, true);
      window.addEventListener('popstate', this.onHistoryNavigation);
      document.addEventListener('visibilitychange', this.onPageHide);
      window.addEventListener('pagehide', this.onPageHide);
      window.addEventListener('pageshow', (event) => {
        if (event.persisted && this.intendedPlaying && this.audio.paused && !this.resumeAfterMetadata) {
          this.tryPlay(true);
        }
      });
    }

    onNavigation(event) {
      if (event.type === 'submit') {
        this.saveState(true);
        return; // POST and form submissions always use Shopify's native behavior.
      }
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!link) return;
      this.saveState(true);
      if (!this.settings.seamlessNavigation || !this.intendedPlaying || this.audio.paused ||
          event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey ||
          event.shiftKey || event.altKey || link.hasAttribute('download') ||
          (link.target && link.target !== '_self') ||
          link.closest('[data-dmp-no-transition], [data-no-transition], [data-turbo="false"], [data-no-instant]') ||
          link.hasAttribute('onclick') || link.href.startsWith('javascript:')) return;
      let destination;
      try { destination = new URL(link.href, location.href); }
      catch { return; }
      if (!this.canSeamlesslyNavigate(destination)) return;
      event.preventDefault();
      this.softNavigate(destination.href, 'push');
    }

    onHistoryNavigation(event) {
      if (!this.didSeamlessNavigate) return;
      if (!this.settings.seamlessNavigation || !this.intendedPlaying || this.audio.paused ||
          !this.canSeamlesslyNavigate(new URL(location.href), true)) {
        location.reload(); // Avoid a stale product template after Back when paused.
        return;
      }
      this.softNavigate(location.href, 'pop', event.state?.dmpScrollY || 0);
    }

    canSeamlesslyNavigate(target, allowSame = false) {
      if (!/^https?:$/.test(target.protocol) || target.origin !== location.origin ||
          target.username || target.password) return false;
      const current = new URL(location.href);
      if (!allowSame && target.pathname === current.pathname) return false; // Keep native variant/filter query handling.
      const root = window.Shopify?.routes?.root || '/';
      const base = root.endsWith('/') ? root : root + '/';
      if (!target.pathname.startsWith(base)) return false;
      const route = target.pathname.slice(base.length);
      // Only regular storefront GET pages. Avoid checkout, carts, customers,
      // filtering/query widgets, payment, apps and sensitive flows.
      if (route && !/^(products|collections|pages|blogs|search)(\/|$)/.test(route)) return false;
      const banned = ['preview_theme_id', '_pos', '_sid', '_ss', 'view', 'sections', 'section_id'];
      if (banned.some(key => target.searchParams.has(key))) return false;
      return true;
    }

    async softNavigate(href, mode = 'push', restoreScrollY = 0) {
      const sequence = ++this.navigationSequence;
      this.navigationController?.abort();
      const controller = new AbortController();
      this.navigationController = controller;
      const timeout = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(href, {
          method: 'GET', credentials: 'same-origin', redirect: 'follow',
          headers: { 'Accept': 'text/html' }, signal: controller.signal
        });
        if (!response.ok || !response.headers.get('content-type')?.includes('text/html'))
          throw new Error('Not an HTML storefront page');
        const finalUrl = new URL(response.url);
        if (!this.canSeamlesslyNavigate(finalUrl, true))
          throw new Error('Redirected outside supported storefront routes');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const oldMain = document.querySelector('#MainContent, main[role="main"], main');
        const newMain = html.querySelector('#MainContent, main[role="main"], main');
        if (!oldMain || !newMain || !this.root.isConnected ||
            !html.querySelector(ROOT_SELECTOR)) throw new Error('Theme navigation is incompatible');
        if (sequence !== this.navigationSequence) return;
        // Fetch CSS for a new template before injecting product/collection content.
        await this.loadStylesFrom(html, finalUrl.href);
        if (sequence !== this.navigationSequence) return;
        // Detach our already-playing player if the merchant placed it within main.
        // Do not destroy/recreate its audio element: that preserves the user gesture.
        if (oldMain.contains(this.root)) {
          const persistent = this.root.closest('.shopify-section') || this.root;
          document.body.appendChild(persistent);
        }
        newMain.querySelectorAll(ROOT_SELECTOR).forEach(node => {
          const wrapper = node.closest('.shopify-section') || node;
          if (newMain.contains(wrapper)) wrapper.remove();
        });
        if (this.isOpen) this.close();
        // Unmount editor-aware sections; then mount the arriving template.
        oldMain.querySelectorAll('.shopify-section').forEach(section => {
          section.dispatchEvent(new CustomEvent('shopify:section:unload', { bubbles: true }));
        });
        oldMain.replaceWith(document.importNode(newMain, true));
        const currentMain = document.querySelector('#MainContent, main[role="main"], main');
        document.title = html.title || document.title;
        this.updateDocumentMeta(html);
        document.body.className = html.body.className;
        for (const attr of Array.from(document.body.attributes)) {
          if (attr.name.startsWith('data-template')) document.body.removeAttribute(attr.name);
        }
        for (const attr of Array.from(html.body.attributes)) {
          if (attr.name.startsWith('data-template')) document.body.setAttribute(attr.name, attr.value);
        }
        if (mode === 'push') {
          history.replaceState({ ...history.state, dmpScrollY: scrollY }, '', location.href);
          history.pushState({ dmpSoftNavigation: true }, '', finalUrl.href);
        }
        await this.loadScriptsFrom(html, currentMain, finalUrl.href);
        currentMain?.querySelectorAll('.shopify-section').forEach(section => {
          section.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
        });
        document.dispatchEvent(new CustomEvent('deco:page:load', { detail: { url: location.href } }));
        window.scrollTo(0, mode === 'pop' ? restoreScrollY : 0);
        this.didSeamlessNavigate = true;
        this.saveState(true);
      } catch (error) {
        if (sequence !== this.navigationSequence) return;
        // Native navigation remains the safety fallback for theme-specific pages.
        if (mode === 'push') { this.saveState(true); location.assign(href); }
        else { location.reload(); }
      } finally {
        clearTimeout(timeout);
        if (this.navigationController === controller) this.navigationController = null;
      }
    }

    loadStylesFrom(nextDocument, newPageUrl) {
      const existing = new Set([...document.head.querySelectorAll('link[rel="stylesheet"][href]')]
        .map(link => new URL(link.href, location.href).href));
      const promises = [];
      nextDocument.head.querySelectorAll('link[rel="stylesheet"][href]').forEach(link => {
        const url = new URL(link.getAttribute('href'), newPageUrl).href;
        if (existing.has(url)) return;
        existing.add(url);
        const clone = document.createElement('link');
        clone.rel = 'stylesheet';
        clone.href = url;
        if (link.media) clone.media = link.media;
        promises.push(new Promise(resolve => {
          clone.onload = resolve;
          clone.onerror = resolve;
          setTimeout(resolve, 3500);
        }));
        document.head.appendChild(clone);
      });
      return Promise.all(promises);
    }

    async loadScriptsFrom(nextDocument, currentMain, newPageUrl) {
      // Theme-level scripts may be used by the next template but absent from
      // the current one. Load new external scripts only once per document.
      const loaded = new Set([...document.querySelectorAll('script[src]')]
        .filter(node => !currentMain?.contains(node))
        .map(node => new URL(node.src, location.href).href));
      const incomingScripts = [...nextDocument.querySelectorAll('script[src]')];
      for (const script of incomingScripts) {
        const url = new URL(script.getAttribute('src'), newPageUrl).href;
        if (loaded.has(url) || script.type === 'application/json') continue;
        loaded.add(url);
        await new Promise(resolve => {
          const element = document.createElement('script');
          element.src = url;
          element.async = false;
          if (script.type) element.type = script.type;
          if (script.noModule) element.noModule = true;
          element.onload = resolve; element.onerror = resolve;
          setTimeout(resolve, 4500);
          document.head.appendChild(element);
        });
      }
      // Inline JavaScript in freshly swapped Shopify sections doesn't run
      // automatically through DOMParser. Explicitly activate those scripts.
      if (currentMain) {
        for (const old of [...currentMain.querySelectorAll('script:not([src])')]) {
          if (old.type && !['text/javascript', 'application/javascript', 'module'].includes(old.type)) continue;
          const script = document.createElement('script');
          for (const { name, value } of [...old.attributes]) script.setAttribute(name, value);
          script.textContent = old.textContent;
          old.replaceWith(script);
        }
      }
    }

    updateDocumentMeta(nextDocument) {
      for (const selector of ['meta[name="description"]', 'link[rel="canonical"]']) {
        const current = document.head.querySelector(selector);
        const next = nextDocument.head.querySelector(selector);
        if (!next) { current?.remove(); continue; }
        const copy = document.importNode(next, true);
        if (current) current.replaceWith(copy);
        else document.head.appendChild(copy);
      }
    }

    onPageHide() { this.saveState(true); }

    renderQueue() {
      if (!this.queue) return;
      this.queue.innerHTML = this.tracks.map((track, index) => `
        <button class="dmp-track${index === this.index ? ' dmp-current' : ''}"
          type="button" data-track-index="${index}"
          aria-label="Play ${escapeHTML(track.title)} by ${escapeHTML(track.artist)}">
          <span class="dmp-track-art">${track.cover
            ? `<img src="${escapeHTML(track.cover)}" alt="" width="240" height="148" loading="lazy">`
            : ''}</span>
          <span class="dmp-track-copy-small">
            <span class="dmp-track-title">${escapeHTML(track.title)}</span>
            <span class="dmp-track-artist">${escapeHTML(track.artist)}</span>
          </span>
          <span class="dmp-track-index">${String(index + 1).padStart(2, '0')}</span>
        </button>`).join('');
    }

    onQueueClick(event) {
      const target = event.target.closest('[data-track-index]');
      if (!target) return;
      const index = Number(target.dataset.trackIndex);
      if (index === this.index && !this.audio.paused) {
        this.setView('now');
        return;
      }
      if (index === this.index) this.tryPlay(false);
      else this.select(index, true);
      this.setView('now');
    }

    paintTrack(index) {
      const track = this.tracks[index];
      if (!track) return;
      this.root.style.setProperty('--dmp-accent', track.accent || '#f3f3f3');
      this.root.style.setProperty('--dmp-accent-2', track.accent2 || '#d7d7d7');
      if (this.title) this.title.textContent = track.title || 'Untitled';
      if (this.artist) this.artist.textContent = track.artist || '';
      for (const image of [this.cover, this.launcherArt, this.ambientImage]) {
        if (!image) continue;
        if (track.cover) { image.src = track.cover; image.hidden = false; }
        else { image.removeAttribute('src'); image.hidden = true; }
      }
      if (this.cover) this.cover.alt = track.cover ? `${track.title} cover artwork` : '';
      this.updateCurrentQueueItem();
      this.updateMediaMetadata(track);
    }

    ensureSource(position = null) {
      if (!this.tracks.length || !this.tracks[this.index]) return false;
      if (this.sourceIndex !== this.index) {
        this.pendingPosition = position === null ? null : Math.max(0, Number(position) || 0);
        this.sourceIndex = this.index;
        this.audio.src = this.tracks[this.index].audio;
        this.audio.load();
      } else if (position !== null) {
        this.pendingPosition = Math.max(0, Number(position) || 0);
        this.applyPendingPosition();
      }
      return true;
    }

    applyPendingPosition() {
      if (this.pendingPosition === null || this.audio.readyState < 1) return false;
      const duration = this.audio.duration;
      const position = Number.isFinite(duration) && duration > 0
        ? clamp(this.pendingPosition, 0, Math.max(0, duration - 0.1))
        : Math.max(0, this.pendingPosition);
      try {
        this.audio.currentTime = position;
        this.pendingPosition = null;
        return true;
      } catch { return false; }
    }

    tryPlay(automatic = false) {
      if (!this.ensureSource()) {
        this.notify('Add an audio URL to begin playing music.');
        return;
      }
      this.intendedPlaying = true;
      this.resumeAfterMetadata = false;
      const request = ++this.playRequest;
      this.saveState(true);
      let result;
      try { result = this.audio.play(); }
      catch (error) { this.onPlayRejected(error, request, automatic); return; }
      if (result && typeof result.catch === 'function') {
        result.catch((error) => this.onPlayRejected(error, request, automatic));
      }
    }

    onPlayRejected(error, request, automatic) {
      if (this.destroyed || request !== this.playRequest || !this.intendedPlaying) return;
      if (error?.name === 'AbortError') return;
      if (error?.name === 'NotAllowedError') {
        // A true page reload can be blocked by the browser even after earlier
        // interaction. Never falsely display a playing state or auto-mute.
        // Keep the saved play preference and timestamp for future navigation.
        if (!automatic) this.notify('Playback was blocked by your browser.');
      } else {
        this.notify('Unable to play this song. Check the audio URL.');
      }
      // Never erase the PLAY preference just because a browser blocks autoplay.
      this.saveState(true);
    }

    pauseByUser() {
      ++this.playRequest;
      this.intendedPlaying = false;
      this.resumeAfterMetadata = false;
      this.audio.pause();
      this.updatePlayUI();
      this.saveState(true);
    }
    toggle() { this.audio.paused ? this.tryPlay(false) : this.pauseByUser(); }

    select(index, autoplay = true) {
      if (!Number.isInteger(index) || index < 0 || index >= this.tracks.length) return;
      ++this.playRequest;
      this.audio.pause();
      this.index = index;
      this.intendedPlaying = autoplay;
      this.resumeAfterMetadata = false;
      this.pendingPosition = 0;
      this.sourceIndex = -1;
      // New tracks must start at zero, even before metadata arrives.
      // Keep the pending zero when saving navigation state immediately.
      this.ensureSource(0);
      if (this.currentText) this.currentText.textContent = '0:00';
      if (this.durationText) this.durationText.textContent = '0:00';
      if (this.progress) this.progress.value = '0';
      this.updateRangeFill(this.progress, 0);
      this.paintTrack(index);
      this.setView('now');
      this.saveState(true);
      if (autoplay) this.tryPlay(false);
      else this.updatePlayUI();
    }

    previous() {
      if (this.audio.currentTime > 4) {
        this.audio.currentTime = 0;
        this.saveState(true);
        return;
      }
      this.select((this.index + this.tracks.length - 1) % this.tracks.length, true);
    }

    next(userInitiated = true) {
      if (!this.tracks.length) return;
      let nextIndex;
      if (this.shuffle && this.tracks.length > 1) {
        do { nextIndex = Math.floor(Math.random() * this.tracks.length); }
        while (nextIndex === this.index);
      } else {
        nextIndex = this.index + 1;
        if (nextIndex >= this.tracks.length) {
          if (userInitiated || this.repeatMode === 'all') nextIndex = 0;
          else { this.pauseByUser(); this.audio.currentTime = 0; return; }
        }
      }
      this.select(nextIndex, true);
    }

    notify(message) {
      if (!this.status) return;
      clearTimeout(this.statusTimer);
      this.status.textContent = message || '';
      if (message) this.statusTimer = setTimeout(() => { this.status.textContent = ''; }, 4500);
    }
    updatePlayUI() {
      const playing = !this.audio.paused && !this.audio.ended;
      this.root.classList.toggle('dmp-is-playing', playing);
      this.toggleButtons.forEach((button) => button.setAttribute('aria-label', playing ? 'Pause' : 'Play'));
      if ('mediaSession' in navigator) {
        try { navigator.mediaSession.playbackState = playing ? 'playing' : 'paused'; } catch { /* Browser-specific. */ }
      }
    }
    updateModeUI() {
      this.root.classList.toggle('dmp-is-muted', this.audio.muted || this.audio.volume === 0);
      this.shuffleButton?.classList.toggle('dmp-active', this.shuffle);
      this.repeatButton?.classList.toggle('dmp-active', this.repeatMode !== 'off');
      this.repeatButton?.setAttribute('aria-label', this.repeatMode === 'one'
        ? 'Repeat one' : this.repeatMode === 'all' ? 'Repeat all' : 'Repeat off');
      if (this.repeatLabel) this.repeatLabel.textContent = this.repeatMode === 'one'
        ? 'Repeat 1' : this.repeatMode === 'all' ? 'Repeat All' : 'Repeat';
    }
    setView(view) {
      const current = view === 'queue' ? 'queue' : 'now';
      this.root.classList.toggle('dmp-view-now', current === 'now');
      this.root.classList.toggle('dmp-view-queue', current === 'queue');
      this.viewButtons.forEach((button) => {
        const active = button.getAttribute('data-dmp-view') === current;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }
    updateRangeFill(input, value) {
      if (input) input.style.setProperty('--dmp-value', `${clamp(value, 0, 100)}%`);
    }
    updateTimeline(overridePosition = null) {
      if (this.isSeeking || !this.progress) return;
      const duration = this.audio.duration;
      const position = overridePosition === null ? this.savedPosition() : overridePosition;
      if (!Number.isFinite(duration) || duration <= 0) {
        this.progress.value = '0';
        if (this.currentText) this.currentText.textContent = formatTime(position);
        this.updateRangeFill(this.progress, 0);
        return;
      }
      const ratio = clamp(position / duration, 0, 1);
      this.progress.value = String(Math.round(ratio * 1000));
      if (this.currentText) this.currentText.textContent = formatTime(position);
      if (this.durationText) this.durationText.textContent = formatTime(duration);
      this.updateRangeFill(this.progress, ratio * 100);
    }
    updateCurrentQueueItem() {
      this.queue?.querySelectorAll('[data-track-index]').forEach((item) => {
        const active = Number(item.dataset.trackIndex) === this.index;
        item.classList.toggle('dmp-current', active);
        item.setAttribute('aria-current', String(active));
      });
    }

    open() {
      if (this.isOpen) return;
      clearTimeout(this.closeTimer);
      this.isOpen = true;
      this.lastFocused = document.activeElement;
      this.scrim.hidden = false;
      // CSS requires these two classes (not a class on the root).
      this.scrim.classList.add('dmp-visible');
      this.panel.classList.add('dmp-open');
      this.panel.inert = false;
      this.panel.setAttribute('aria-hidden', 'false');
      this.openButton.setAttribute('aria-expanded', 'true');
      document.body.classList.add('dmp-lock');
      this.closeButton?.focus({ preventScroll: true });
    }
    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.scrim.classList.remove('dmp-visible');
      this.panel.classList.remove('dmp-open');
      this.panel.inert = true;
      this.panel.setAttribute('aria-hidden', 'true');
      this.openButton.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('dmp-lock');
      this.closeTimer = window.setTimeout(() => {
        if (!this.isOpen) this.scrim.hidden = true;
      }, 350);
      this.lastFocused?.focus?.({ preventScroll: true });
    }
    onKeydown(event) {
      if (event.key === 'Escape' && this.isOpen) {
        event.preventDefault();
        this.close();
        return;
      }
      if (!this.isOpen) return;
      if (event.key === 'Tab') { this.trapFocus(event); return; }
      const target = event.target;
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target?.isContentEditable) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.code === 'Space' && !(target instanceof HTMLButtonElement)) {
        event.preventDefault(); this.toggle();
      } else if (event.key.toLowerCase() === 'm') {
        event.preventDefault(); this.audio.muted = !this.audio.muted; this.updateModeUI(); this.saveState(true);
      } else if (event.key === 'ArrowRight' && this.ensureSource()) {
        event.preventDefault();
        this.audio.currentTime = Math.min(this.audio.duration || Infinity, this.audio.currentTime + 10);
      } else if (event.key === 'ArrowLeft' && this.ensureSource()) {
        event.preventDefault();
        this.audio.currentTime = Math.max(0, this.audio.currentTime - 10);
      }
    }
    trapFocus(event) {
      const elements = [...this.panel.querySelectorAll('button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((element) => !element.hidden && element.offsetParent !== null);
      if (!elements.length) return;
      const first = elements[0], last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    }
    updateMediaMetadata(track) {
      if (!('mediaSession' in navigator) || typeof MediaMetadata === 'undefined') return;
      try {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: track.title || 'Untitled', artist: track.artist || 'DECO',
          album: 'DECO SOUND', artwork: track.cover ? [{ src: track.cover, sizes: '512x512' }] : []
        });
        const session = navigator.mediaSession;
        session.setActionHandler('play', () => this.tryPlay(false));
        session.setActionHandler('pause', () => this.pauseByUser());
        session.setActionHandler('previoustrack', () => this.previous());
        session.setActionHandler('nexttrack', () => this.next(true));
        session.setActionHandler('seekbackward', (details) => {
          this.audio.currentTime = Math.max(0, this.audio.currentTime - (details.seekOffset || 10));
        });
        session.setActionHandler('seekforward', (details) => {
          this.audio.currentTime = Math.min(this.audio.duration || Infinity,
            this.audio.currentTime + (details.seekOffset || 10));
        });
        session.setActionHandler('seekto', (details) => {
          if (Number.isFinite(details.seekTime)) this.audio.currentTime = details.seekTime;
        });
      } catch { /* Media Session support differs by platform. */ }
    }
    updateMediaPosition() {
      if (!('mediaSession' in navigator) || !navigator.mediaSession.setPositionState) return;
      if (!Number.isFinite(this.audio.duration) || this.audio.duration <= 0) return;
      try {
        navigator.mediaSession.setPositionState({
          duration: this.audio.duration,
          playbackRate: this.audio.playbackRate,
          position: clamp(this.audio.currentTime, 0, this.audio.duration)
        });
      } catch { /* Optional enhancement only. */ }
    }
    destroy() {
      this.onPageHide();
      this.destroyed = true;
      clearTimeout(this.saveTimer);
      clearTimeout(this.statusTimer);
      clearTimeout(this.closeTimer);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onNavigation);
      document.removeEventListener('submit', this.onNavigation, true);
      window.removeEventListener('popstate', this.onHistoryNavigation);
      this.navigationController?.abort();
      document.removeEventListener('visibilitychange', this.onPageHide);
      window.removeEventListener('pagehide', this.onPageHide);
      this.queue?.removeEventListener('click', this.onQueueClick);
      this.audio?.pause();
      document.body.classList.remove('dmp-lock');
    }
  }

  const instances = new WeakMap();
  function mount(scope = document) {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...scope.querySelectorAll(ROOT_SELECTOR));
    roots.forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new DecoMusicPlayer(root));
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  } else mount();
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const scope = event.target;
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...scope.querySelectorAll(ROOT_SELECTOR));
    roots.forEach((root) => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });
  });
})();
